import React, { useCallback, useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { Recipe } from '@library/menu-service'
import { MenuApiPreferencesResponse } from '@library/menu-service/queries/menuApiPreferences/types'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useIsMenuPersonalisationEnabled } from 'hooks/useIsMenuPersonalisationEnabled'
import { useIsProteinOnSignupEnabled } from 'hooks/useIsProteinOnSignupEnabled'
import { useStaticArray } from 'hooks/useStaticArray'
import {
  menuClosePropositionTileAction,
  selectRecipeVariant,
  showDetailRecipe,
  trackCloseThePropositionTile,
  trackShowThePropositionTile,
} from 'routes/Menu/actions/menuRecipeDetails'
import { CaloriesTutorial } from 'routes/Menu/components/MenuPreferences'
import { useBasket } from 'routes/Menu/domains/basket'
import { CollectionId } from 'routes/Menu/domains/collections/constants'
import { useSetBrowserCTAVisibility, useCollectionRecipes } from 'routes/Menu/domains/menu'
import { getHasClosedPropositionTile } from 'routes/Menu/selectors/menu'

import { useAuth } from '../../domains/auth/useAuth'
import { CTAToAllRecipes } from '../CTAToAllRecipes'
import { PropositionTile } from './PropositionTile'
import { RecipeTile } from './RecipeTileWrapper'
import { useTrackRecipeListEffect } from './useTrackRecipeListEffect'
import { useTrackSoldOutRecipesEffect } from './useTrackSoldOutRecipesEffect'

import css from './RecipeList.css'

export const RecipeList = ({
  menuPreferences,
}: {
  menuPreferences?: MenuApiPreferencesResponse
}) => {
  const currentCollectionId = useCurrentCollectionId()

  const recipeSets = useCollectionRecipes(currentCollectionId)

  const immutableRecipes = useStaticArray(recipeSets.map(({ recipe }) => recipe))
  useTrackSoldOutRecipesEffect(immutableRecipes)
  useTrackRecipeListEffect(currentCollectionId, immutableRecipes)

  const dispatch = useDispatch()
  const { isAuthenticated } = useAuth()

  const isMenuPersonalisationEnabled = useIsMenuPersonalisationEnabled()
  const isProteinOnSignupEnabled = useIsProteinOnSignupEnabled()

  const hasClosedTile = useSelector(getHasClosedPropositionTile)

  const [isOnAllRecipesCategory, setIsOnAllRecipesCategory] = useState(
    currentCollectionId === CollectionId.AllRecipes,
  )
  const [showPropositionTile, setShowPropositionTile] = useState(
    isMenuPersonalisationEnabled && !isAuthenticated && !hasClosedTile && isOnAllRecipesCategory,
  )

  useEffect(() => {
    const isOnAllRecipes = currentCollectionId === CollectionId.AllRecipes
    setIsOnAllRecipesCategory(isOnAllRecipes)
    setShowPropositionTile(
      isMenuPersonalisationEnabled && !isAuthenticated && !hasClosedTile && isOnAllRecipes,
    )
  }, [currentCollectionId, isAuthenticated, hasClosedTile, isMenuPersonalisationEnabled])

  useEffect(() => {
    if (showPropositionTile) {
      dispatch(trackShowThePropositionTile())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeTile = () => {
    setShowPropositionTile(false)
    dispatch(menuClosePropositionTileAction())
    dispatch(trackCloseThePropositionTile())
  }

  const { canAddRecipes, addRecipe, removeRecipe, reachedLimit } = useBasket()

  const renderPropositionTile = () => <PropositionTile onClose={closeTile} />

  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()

  const onClick = useCallback((recipeUuid) => dispatch(showDetailRecipe(recipeUuid)), [dispatch])

  const selectVariant = useCallback(
    ({
      variant,
      variantOutOfStock,
      originalRecipeId,
      recipeReference,
    }: {
      variant: Recipe
      variantOutOfStock: boolean
      originalRecipeId: string
      recipeReference: string
    }) => {
      dispatch(
        selectRecipeVariant({
          originalRecipeId,
          variant,
          collectionId: currentCollectionId,
          variantOutOfStock,
          view: 'grid',
          close: true,
          recipeReference,
        }),
      )
    },
    [currentCollectionId, dispatch],
  )

  return (
    <div className={css.threeTileRecipeList}>
      {showPropositionTile && renderPropositionTile()}

      {recipeSets.map((value, index: number) => (
        <React.Fragment key={value.reference}>
          {index === 5 && <CaloriesTutorial />}
          <RecipeTile
            recipe={value.recipe}
            currentCollectionId={currentCollectionId}
            onClick={onClick}
            menuPreferences={menuPreferences}
            setBrowserCTAVisible={setBrowserCTAVisible}
            canAddRecipes={canAddRecipes}
            addRecipe={addRecipe}
            removeRecipe={removeRecipe}
            reachedLimit={reachedLimit}
            selectVariant={selectVariant}
            recipeReference={value.reference}
            originalId={value.originalId}
            isProteinOnSignupEnabled={isProteinOnSignupEnabled}
          />
        </React.Fragment>
      ))}
      <CTAToAllRecipes />
    </div>
  )
}
